import {
  BlogPostEntity,
  IconPageEntity,
} from "@/common/contracts/strapi.contracts";
import { RETURN_POLICY_ROUTES } from "@/common/static/footer.static";
import {
  AskingPriceLike,
  resolveAskingPrice,
} from "@/common/utils/asking-price.utils";
import { decodeHtmlEntities } from "@/common/utils/html-entities.utils";
import { buildProductImageAlt } from "@/common/utils/product-image.utils";
import { isSold as isSoldAuction } from "@/common/utils/sold-product.utils";
import { withLocale } from "@/common/utils/url.utils";
import { CANONICAL_HOST } from "@/config";
import { SUPPORTED_COUNTRIES } from "@/constants/countries";
import {
  type ContentLocale,
  defaultLocale,
  toContentLocale,
} from "@/i18n/config";

export const whoppahPublisher = {
  "@type": "Organization",
  name: "Whoppah",
  logo: {
    "@type": "ImageObject",
    url: `${CANONICAL_HOST}/logo.png`,
  },
};

/**
 * Canonical Whoppah Organization reference used as the `seller`
 * on Product schemas and as the `publisher` everywhere else.
 *
 * The @id matches the one emitted from pages/_document.js so that
 * Google can graph-merge our schemas across page types.
 */
const whoppahOrgRef = {
  "@type": "Organization",
  "@id": `${CANONICAL_HOST}/#org`,
  name: "Whoppah",
};

/**
 * Canonical author reference for editorial content (icons, blog,
 * how-to guides). Resolves to /about/evelien once that page ships;
 * until then the URL is a stable identifier in the entity graph.
 *
 * Switching the icon Article schema author from Whoppah Organization
 * to a named Person is an E-E-A-T signal: Google and LLMs both rank
 * editorial content authored by a named expert more highly than
 * content attributed to a faceless organisation.
 */
export const evelienAuthor = {
  "@type": "Person",
  "@id": `${CANONICAL_HOST}/about/evelien#person`,
  name: "Evelien Bunnik-Remmelts",
  jobTitle: "Co-Founder and CEO",
  url: `${CANONICAL_HOST}/about/evelien`,
  sameAs: ["https://www.linkedin.com/in/evelien-remmeltscpowhoppah/"],
  worksFor: whoppahOrgRef,
};

const localizePath = (path: string, locale: string | undefined) =>
  withLocale(path, locale, defaultLocale);

const absoluteUrl = (path: string) => `${CANONICAL_HOST}${path}`;

/** The product path word per content language; `/en-us` reads `en` (WHO-5766). */
const PRODUCT_PATH_WORD: Record<ContentLocale, string> = {
  en: "products",
  nl: "producten",
  de: "produkte",
  fr: "produits",
  es: "productos",
  it: "prodotti",
};

const productPath = (slug: string, locale: string | undefined) => {
  const effectiveLocale = locale || defaultLocale;
  const word = PRODUCT_PATH_WORD[toContentLocale(effectiveLocale)];

  return localizePath(`/${word}/${slug}`, effectiveLocale);
};

export const buildBlogPostingSchema = (blogPost: BlogPostEntity) => {
  const attributes = blogPost.attributes;
  if (!attributes) {
    return null;
  }

  const {
    seo,
    publishedAt,
    updatedAt,
    title,
    description,
    image,
    author,
    slug,
  } = attributes;

  const locale = attributes.locale || defaultLocale;
  const blogPath = withLocale(`/blog/${slug}`, locale, defaultLocale);
  const canonicalUrl = `${CANONICAL_HOST}${blogPath}`;
  const imageAttributes = image?.data?.attributes;
  const authorName = author?.data?.attributes?.name;

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
    headline: title,
    description: seo?.description ?? description,
    ...(imageAttributes?.url
      ? {
          image: {
            "@type": "ImageObject",
            url: imageAttributes.url,
            ...(imageAttributes.width && { width: imageAttributes.width }),
            ...(imageAttributes.height && { height: imageAttributes.height }),
          },
        }
      : {}),
    datePublished: publishedAt,
    // Strapi bumps updatedAt on every save; the build time is never a truthful
    // modification date. Fall back to publishedAt for never-edited posts.
    dateModified: updatedAt ?? publishedAt,
    ...(authorName
      ? { author: { "@type": "Person", name: authorName } }
      : { author: whoppahPublisher }),
    publisher: whoppahPublisher,
    inLanguage: locale,
  };
};

export const buildIconArticleSchema = (icon: IconPageEntity) => {
  const attributes = icon.attributes;
  if (!attributes) {
    return null;
  }

  const { seo, name, title, slug, mainImage, publishedAt, updatedAt } =
    attributes;

  if (!slug || !name) {
    return null;
  }

  const locale = attributes.locale || defaultLocale;
  const iconPath = withLocale(`/icon/${slug}`, locale, defaultLocale);
  const canonicalUrl = `${CANONICAL_HOST}${iconPath}`;
  const imageUrl =
    mainImage?.data?.attributes?.url ?? seo?.image?.data?.attributes?.url;

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
    headline: seo?.title ?? name,
    description: seo?.description ?? title ?? name,
    ...(imageUrl
      ? {
          image: {
            "@type": "ImageObject",
            url: imageUrl,
          },
        }
      : {}),
    ...(publishedAt ? { datePublished: publishedAt } : {}),
    ...(updatedAt ? { dateModified: updatedAt } : {}),
    author: evelienAuthor,
    publisher: whoppahPublisher,
    inLanguage: locale,
  };
};

/**
 * Extract Q&A pairs from icon-page markdown body fields.
 *
 * Strapi delivers icon-page copy as 3 markdown fields (mainContent,
 * fullContent, secondaryContent). When editors use a question-shaped
 * heading (`## What makes the Eames Lounge Chair iconic?`) followed
 * by the answer text, we can lift those into a FAQPage block.
 *
 * Heading is considered a "question" when it starts with one of the
 * usual interrogative words OR ends with a question mark. The answer
 * is everything between this heading and the next heading.
 *
 * We deliberately keep the parser tiny: no markdown library, no AST.
 * so it's cheap to run at SSR and resilient to typos. If editors
 * don't use Q-shaped headings yet, the function returns an empty
 * list and no FAQPage is emitted.
 */
const QUESTION_PREFIXES = [
  "what",
  "how",
  "why",
  "when",
  "where",
  "which",
  "is",
  "are",
  "do",
  "does",
  "can",
  "should",
  "who",
];

export const looksLikeQuestion = (heading: string) => {
  const lower = heading.trim().toLowerCase();
  if (lower.endsWith("?")) {
    return true;
  }

  return QUESTION_PREFIXES.some(prefix => lower.startsWith(`${prefix} `));
};

const stripMarkdownArtifacts = (text: string) =>
  text
    // Remove image markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    // Resolve link markdown to its label
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    // Strip lingering bold/italic markers
    .replace(/[*_]{1,3}/g, "")
    .replace(/`/g, "")
    .replace(/^>\s*/gm, "")
    .replace(/\s+/g, " ")
    .trim();

export const extractFaqFromMarkdown = (
  ...markdownBlocks: Array<string | null | undefined>
) => {
  const headingRegex = /^(#{2,4})\s+(.+?)\s*$/gm;
  const faqItems: Array<{ question: string; answer: string }> = [];

  for (const rawBlock of markdownBlocks) {
    if (!rawBlock) {
      continue;
    }

    /*
     * `##Heading` (no space) is how several legacy Strapi pages were written. The visible
     * block normalises it before rendering, so the schema has to see the same headings or the
     * accordion and the FAQPage markup disagree about which questions the page asks.
     */
    const block = rawBlock.replace(/^(#{1,6})(?=[^#\s])/gm, "$1 ");

    const headings: Array<{ index: number; raw: string; text: string }> = [];
    let match: RegExpExecArray | null;
    headingRegex.lastIndex = 0;
    while ((match = headingRegex.exec(block)) !== null) {
      // match[0] is always present here because we just matched.
      headings.push({
        index: match.index,
        raw: match[0]!,
        text: match[2] ?? "",
      });
    }
    headings.forEach((heading, i) => {
      if (!looksLikeQuestion(heading.text)) {
        return;
      }

      const start = heading.index + heading.raw.length;
      const next = headings[i + 1];
      const end = next ? next.index : block.length;
      const rawAnswer = block.slice(start, end);
      const answer = stripMarkdownArtifacts(rawAnswer);

      if (!answer) {
        return;
      }

      faqItems.push({
        question: heading.text.replace(/^[#\s]+/, "").trim(),
        answer,
      });
    });
  }

  return faqItems;
};

/**
 * Emit a FAQPage schema for an /icon/ page. Reads Q&A pairs from
 * the page's markdown bodies; if no question-shaped headings are
 * present, returns null and no FAQPage script is rendered.
 *
 * This unlocks FAQ rich results in Google for icon pages without
 * requiring a Strapi schema change.
 */
export const buildIconFaqSchema = (icon: IconPageEntity) => {
  const attributes = icon.attributes;

  if (!attributes) {
    return null;
  }

  const { mainContent, fullContent, secondaryContent } = attributes as {
    mainContent?: string | null;
    fullContent?: string | null;
    secondaryContent?: string | null;
  };

  const faqItems = extractFaqFromMarkdown(
    mainContent,
    fullContent,
    secondaryContent
  );

  if (faqItems.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: answer,
      },
    })),
  };
};

/**
 * Shared shape for a brand-index or designer-index entity.
 * Both `Brand` and `Designer` from the GraphQL gateway already
 * carry the same useful fields (id, title, image, listingPage.url).
 */
type IndexableEntity = {
  id: string | number;
  title: string;
  image?: string | null;
  listingPage?: { url?: string | null } | null;
};

const indexEntityUrl = (
  entity: IndexableEntity,
  locale: string | undefined
) => {
  if (entity.listingPage?.url) {
    return absoluteUrl(localizePath(`/${entity.listingPage.url}`, locale));
  }
  return undefined;
};

/**
 * Emit ItemList schema for the /brands index page.
 *
 * Each list item is the canonical URL of that brand's listing page
 * on Whoppah, so Google understands the index is a navigation hub
 * and the brand entities are reachable.
 */
export const buildBrandsOverviewItemListSchema = (
  brands: IndexableEntity[],
  locale: string | undefined,
  pageUrl: string
) => {
  const items = brands
    .map((brand, position) => {
      const url = indexEntityUrl(brand, locale);

      if (!url) {
        return null;
      }

      return {
        "@type": "ListItem",
        position: position + 1,
        url,
        name: brand.title,
      };
    })
    .filter(Boolean);

  if (items.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${pageUrl}#brand-list`,
    name: "Brands on Whoppah",
    itemListElement: items,
  };
};

/**
 * Emit ItemList schema for the /designers index page.
 * Same shape as brands but with `Person` semantics: the entities
 * in the list are designers (people), not brands.
 */
export const buildDesignersOverviewItemListSchema = (
  designers: IndexableEntity[],
  locale: string | undefined,
  pageUrl: string
) => {
  const items = designers
    .map((designer, position) => {
      const url = indexEntityUrl(designer, locale);

      if (!url) {
        return null;
      }

      return {
        "@type": "ListItem",
        position: position + 1,
        url,
        name: designer.title,
      };
    })
    .filter(Boolean);

  if (items.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${pageUrl}#designer-list`,
    name: "Designers on Whoppah",
    itemListElement: items,
  };
};

/**
 * Minimal PLP item shape consumed by `buildPlpCollectionPageSchema`.
 * We deliberately don't import the full GraphQL `Product` type here
 * to keep this utility decoupled from Apollo schema generation:
 * the search results component picks the few fields it has.
 */
export type PlpItemListEntry = {
  id?: string | number | null;
  title?: string | null;
  slug?: string | null;
  url?: string | null;
  image?: string | null;
  price?: { amount?: number | null; currency?: string | null } | null;
};

type PlpCollectionPageInput = {
  title: string;
  description?: string | null;
  /**
   * Locale-less page path, i.e. Next's `asPath` minus its query string.
   * Localized here rather than by the caller: emitting the unprefixed
   * path made the CollectionPage self-reference a URL that 308-redirects
   * to the English page, so Google discarded the whole entity.
   */
  path: string;
  locale: string | undefined;
  totalCount?: number;
  items: PlpItemListEntry[];
};

/**
 * Emit CollectionPage + ItemList schema for a PLP.
 *
 * Why this is correct for a one-off secondhand marketplace:
 * each PLP URL (e.g. /lighting/wall-lights) is a stable, evergreen
 * "page about a category". The page identity doesn't change when
 * individual listings sell: only the items in the collection do.
 *
 * We therefore mark the page itself as a CollectionPage (stable
 * identity, suitable for indexing) and emit an ItemList of URLs to
 * the first ~24 currently-visible listings.
 *
 * Important: do not nest Product entities here. Google Search Console
 * attributes Product rich-result items to the page that contains the
 * markup, so nesting Products on PLPs makes category/listing pages
 * appear as Product snippet URLs.
 */
export const buildPlpCollectionPageSchema = ({
  title,
  description,
  path,
  locale,
  totalCount,
  items,
}: PlpCollectionPageInput) => {
  const url = absoluteUrl(localizePath(path, locale));

  const listElements = items
    .map((item, position) => {
      const itemUrl = item.url
        ? item.url
        : item.slug
        ? absoluteUrl(productPath(item.slug, locale))
        : null;

      if (!itemUrl || !item.title) {
        return null;
      }

      return {
        "@type": "ListItem",
        position: position + 1,
        url: itemUrl,
      };
    })
    .filter(Boolean);

  if (listElements.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${url}#collection`,
    url,
    name: title,
    ...(description ? { description } : {}),
    inLanguage: locale || defaultLocale,
    isPartOf: {
      "@type": "WebSite",
      "@id": `${CANONICAL_HOST}/#website`,
    },
    mainEntity: {
      "@type": "ItemList",
      "@id": `${url}#item-list`,
      name: title,
      ...(typeof totalCount === "number" ? { numberOfItems: totalCount } : {}),
      itemListElement: listElements,
    },
  };
};

/**
 * Emit FAQPage schema for a PLP whose Strapi listing-page description
 * contains question-shaped headings
 * (`## Are the designer bags on Whoppah authentic?`).
 *
 * Same derivation rule as `buildIconFaqSchema`: the visible passages
 * are the single source of truth and the schema is only emitted when
 * they exist, so the markup can never claim something the page does
 * not visibly say. Google retired FAQ rich results, but AI crawlers
 * still consume FAQPage as a structured question/answer source.
 *
 * The @id is anchored to the localized page URL (same rule as the
 * CollectionPage @id) so each locale's Q&A block is its own entity.
 */
export const buildListingPageFaqSchema = (
  description: string | null | undefined,
  path: string,
  locale: string | undefined
) => {
  const faqItems = extractFaqFromMarkdown(description);

  if (faqItems.length === 0) {
    return null;
  }

  const url = absoluteUrl(localizePath(path, locale));

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    inLanguage: locale || defaultLocale,
    mainEntity: faqItems.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: answer,
      },
    })),
  };
};

/**
 * Enriched Product schema for PDPs.
 *
 * Differences vs the legacy `ProductUtils.getRichSnippet()`:
 *  - category derived from product.categories taxonomy path
 *  - material, color extracted from product.attributes
 *  - additionalProperty[] for designer, era/style, dimensions
 *  - brand always present (falls back to product.brand_suggestion
 *    or "Unbranded" when no Brand attribute is set)
 *  - seller is the Whoppah Organization (not the private seller),
 *    so AI shopping engines associate listings with the marketplace
 *  - itemCondition mapped from the product.condition enum when present
 *
 * Retail-only fields intentionally omitted because they don't apply
 * to one-off secondhand listings:
 *  - sku / GTIN / MPN: every listing is unique; slug identifies it
 *  - priceValidUntil: when the item sells the URL goes away
 *  - shippingDetails: rates depend on the buyer's address and the PDP
 *    and checkout bands must agree; not emitted at all (WHO-6290)
 *
 * hasMerchantReturnPolicy is emitted for business sellers only (WHO-5927),
 * see `buildMerchantReturnPolicy`.
 *  - aggregateRating / review: no per-listing reviews on a one-off
 *    marketplace; would invite spammy / invalid Rich Results
 */

type AnyProductAttribute = {
  __typename?: string;
  title?: string | null;
  slug?: string | null;
};

type AnyProduct = {
  id?: string | number | null;
  title?: string | null;
  slug?: string | null;
  description?: string | null;
  condition?: string | null;
  brand_suggestion?: string | null;
  identifier?: string | null;
  images?: Array<{
    url?: string | null;
    width?: number | null;
    height?: number | null;
  } | null> | null;
  attributes?: Array<AnyProductAttribute | null> | null;
  categories?: Array<{
    slug?: string | null;
    title?: string | null;
    route?: string | null;
    level?: number | null;
  } | null> | null;
  width?: number | null;
  height?: number | null;
  depth?: number | null;
  seat_height?: number | null;
  weight?: number | null;
  /**
   * Top-level asking price. Deprecated in the gateway schema in favour of
   * `auction.buy_now_price`; `resolveAskingPrice` tries it second.
   */
  buy_now_price?: AskingPriceLike;
  /** Only `type` is read: it decides the return policy. */
  merchant?: { type?: string | null } | null;
  auction?: {
    /**
     * Availability is decided by this, not by `sold_at`. See the
     * availability block in buildProductSchema. Already selected by
     * ProductFragment; optional here because not every caller's fragment
     * is guaranteed to include it.
     */
    state?: string | null;
    // `allow_bid` is deliberately absent here. Branching the offer price on
    // it is what published the bid floor under a page showing the buy-now
    // price, so leaving it off the type makes that mistake a compile error
    // rather than a live schema. `resolveAskingPrice` needs no flag.
    sold_at?: AskingPriceLike;
    buy_now_price?: AskingPriceLike;
    minimum_bid?: AskingPriceLike;
  } | null;
};

const findAttribute = (
  product: AnyProduct,
  typename: string
): AnyProductAttribute | undefined => {
  const list = product.attributes ?? [];
  return list.find(
    (attribute): attribute is AnyProductAttribute =>
      !!attribute && attribute.__typename === typename
  );
};

const findAttributes = (product: AnyProduct, typename: string) => {
  const list = product.attributes ?? [];
  return list.filter(
    (attribute): attribute is AnyProductAttribute =>
      !!attribute && attribute.__typename === typename
  );
};

/**
 * Google truncates `Product.description` at roughly 5000 characters and
 * reads it as plain text, so seller-written HTML tags and runs of
 * whitespace are removed before the cut.
 */
export const PRODUCT_DESCRIPTION_MAX_LENGTH = 5000;

const toPlainDescription = (description?: string | null) => {
  if (!description) {
    return undefined;
  }

  const plain = description
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!plain) {
    return undefined;
  }

  return plain.slice(0, PRODUCT_DESCRIPTION_MAX_LENGTH);
};

/**
 * Serialize a schema for a `<script type="application/ld+json">` that is
 * written with `dangerouslySetInnerHTML`.
 *
 * `JSON.stringify` alone is not safe there: a seller description that
 * contains `</script>` closes the script element and the rest of the
 * JSON is parsed as HTML. The three characters that matter to the HTML
 * parser are emitted as JSON unicode escapes instead, which JSON-LD
 * consumers decode transparently.
 */
export const serializeJsonLd = (schema: unknown) =>
  JSON.stringify(schema)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");

const CENTIMETRE_UNIT_CODE = "CMT";

/** Google's separator for several colors or materials in one value. */
const MULTI_VALUE_SEPARATOR = "/";

const buildQuantitativeValue = (value?: number | null) =>
  typeof value === "number" && value > 0
    ? { "@type": "QuantitativeValue", value, unitCode: CENTIMETRE_UNIT_CODE }
    : undefined;

const conditionToSchema = (condition?: string | null) => {
  // The marketplace is exclusively pre-owned, so UsedCondition is
  // the safe default. When a more specific condition exists we still
  // emit UsedCondition because Google's enum is the narrower set.
  if (!condition) {
    return "https://schema.org/UsedCondition";
  }

  return "https://schema.org/UsedCondition";
};

const buildAdditionalProperty = (product: AnyProduct) => {
  const props: Array<{
    "@type": "PropertyValue";
    name: string;
    value: string;
  }> = [];

  const designer = findAttribute(product, "Designer");
  if (designer?.title) {
    props.push({
      "@type": "PropertyValue",
      name: "Designer",
      value: designer.title,
    });
  }

  const artist = findAttribute(product, "Artist");
  if (artist?.title) {
    props.push({
      "@type": "PropertyValue",
      name: "Artist",
      value: artist.title,
    });
  }

  const style = findAttribute(product, "Style");
  if (style?.title) {
    props.push({
      "@type": "PropertyValue",
      name: "Style",
      value: style.title,
    });
  }

  if (product.width) {
    props.push({
      "@type": "PropertyValue",
      name: "Width",
      value: `${product.width} cm`,
    });
  }
  if (product.height) {
    props.push({
      "@type": "PropertyValue",
      name: "Height",
      value: `${product.height} cm`,
    });
  }
  if (product.depth) {
    props.push({
      "@type": "PropertyValue",
      name: "Depth",
      value: `${product.depth} cm`,
    });
  }
  if (product.seat_height) {
    props.push({
      "@type": "PropertyValue",
      name: "Seat height",
      value: `${product.seat_height} cm`,
    });
  }
  if (product.weight) {
    props.push({
      "@type": "PropertyValue",
      name: "Weight",
      value: `${product.weight} kg`,
    });
  }

  return props;
};

const buildCategoryPath = (product: AnyProduct) => {
  const list = (product.categories ?? [])
    .filter((c): c is { slug?: string | null; level?: number | null } => !!c)
    .filter(c => c.slug)
    .sort((a, b) => (a.level ?? 0) - (b.level ?? 0))
    .map(c => c.slug as string);

  if (list.length === 0) {
    return undefined;
  }

  return list.join(" > ");
};

type ProductImageEntry = { url: string; width?: number; height?: number };

/**
 * The image URL without its resize query (WHO-6188).
 *
 * The gateway hands out `...jpeg?width=1280` or `?height=1280`, which the
 * page keeps for its srcsets. The image sitemap lists the bare original,
 * so JSON-LD names that same URL: one image, one URL, for Google Images.
 */
export const toBareImageUrl = (url: string) => url.split(/[?#]/, 1)[0] ?? url;

/**
 * Emit `image` as ImageObject[] rather than a bare URL array.
 *
 * A bare URL tells Google Images nothing about the photo. ImageObject
 * carries the words: `name` and `caption` are what Google reads when
 * deciding what a photo depicts, and `representativeOfPage` on the first
 * entry is the explicit signal for "this is the primary listing image":
 * the one we want picked over a detail shot of a chair leg. Dimensions
 * are included when known so Google can prefer the large original.
 */
const buildProductImageObjects = (
  product: AnyProduct,
  locale: string | undefined
) => {
  const images = (product.images ?? []).reduce<ProductImageEntry[]>(
    (accepted, image) => {
      if (image?.url) {
        accepted.push({
          url: toBareImageUrl(image.url),
          ...(image.width ? { width: image.width } : {}),
          ...(image.height ? { height: image.height } : {}),
        });
      }
      return accepted;
    },
    []
  );

  return images.map(({ url, ...dimensions }, index) => {
    const alt = buildProductImageAlt(product, index, images.length, locale);

    return {
      "@type": "ImageObject",
      url,
      contentUrl: url,
      ...dimensions,
      name: alt,
      caption: alt,
      ...(index === 0 ? { representativeOfPage: true } : {}),
    };
  });
};

/**
 * Countries outside the EEA that Whoppah ships to. The 14-day right of
 * withdrawal is EU consumer law (plus EEA), so these are left out of
 * `applicableCountry`. Monaco is not in the EEA either.
 */
const NON_EEA_SUPPORTED_COUNTRIES = new Set<string>(["CH", "GB", "MC"]);

export const RETURN_POLICY_COUNTRIES = SUPPORTED_COUNTRIES.filter(
  country => !NON_EEA_SUPPORTED_COUNTRIES.has(country)
);

const BUSINESS_SELLER_RETURN_DAYS = 14;

/**
 * Per-offer return policy (WHO-5927).
 *
 *  - BUSINESS seller: the legal 14-day right of withdrawal from delivery,
 *    return by mail, buyer pays the return shipping.
 *  - INDIVIDUAL seller: nothing. There is no right of withdrawal, but an
 *    item that significantly deviates from its listing (a replica, stains,
 *    an irremovable smell such as smoke) goes back to the seller and the
 *    buyer is refunded. `MerchantReturnNotPermitted` would contradict that,
 *    so no policy is claimed at all (Thomas, 2026-09-28).
 *  - Unknown type: nothing, rather than guessing.
 */
export const buildMerchantReturnPolicy = (
  merchantType: string | null | undefined,
  locale: string | undefined
) => {
  if (merchantType?.toUpperCase() !== "BUSINESS") {
    return null;
  }

  const effectiveLocale = locale || defaultLocale;
  const articlePath =
    RETURN_POLICY_ROUTES[toContentLocale(effectiveLocale)] ??
    RETURN_POLICY_ROUTES.en;

  return {
    "@type": "MerchantReturnPolicy",
    applicableCountry: RETURN_POLICY_COUNTRIES,
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays: BUSINESS_SELLER_RETURN_DAYS,
    returnMethod: "https://schema.org/ReturnByMail",
    returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
    url: absoluteUrl(localizePath(articlePath, effectiveLocale)),
  };
};

const UNBRANDED = "Unbranded";

/**
 * `name` is required on a Product. The title is always there in practice,
 * but when it is not, brand + deepest category is a truthful description
 * ("Vitra Chairs"); with neither there is no valid name and no schema.
 */
const buildProductName = (product: AnyProduct, brandName: string) => {
  const title = product.title ? decodeHtmlEntities(product.title).trim() : "";

  if (title) {
    return title;
  }

  const deepestCategory = (product.categories ?? [])
    .filter(category => !!category?.title?.trim())
    .sort((a, b) => (b?.level ?? 0) - (a?.level ?? 0))[0]?.title;

  return [brandName === UNBRANDED ? "" : brandName, deepestCategory ?? ""]
    .map(part => part.trim())
    .filter(Boolean)
    .join(" ");
};

export const buildProductSchema = (
  product: AnyProduct | undefined | null,
  locale: string | undefined,
  productLocaleDefault: string | undefined
) => {
  if (!product) {
    return null;
  }

  // The price the page shows, resolved the one way the whole codebase
  // resolves it. This used to read the bid floor whenever bidding was
  // allowed, which is most listings, and so advertised a price the page
  // never printed: see `resolveAskingPrice`.
  const displayPrice = resolveAskingPrice(product);

  // No publishable price means no Product schema at all (WHO-5927). An
  // offer without a price is a critical error in both the Product snippets
  // and Merchant listings reports, and a Product without an offer is one
  // too, so the only valid markup for a "price on request" listing (asking
  // price 0) or one with no price data is none.
  if (!displayPrice) {
    return null;
  }

  /**
   * Availability comes from the auction state, never from the order.
   *
   * The rule (Thomas, 2026-08-01): an item is available only while its
   * auction is PUBLISHED, and a sold item is unavailable regardless of
   * whether its order has completed. The backend already agrees: the
   * search indexer writes
   * `"availability": product.auction.state == AuctionState.PUBLISHED`
   * (`catalog/services/search.py`), with no reference to any order.
   *
   * The previous check was `Boolean(auction.sold_at)`, which is
   * order-derived: `getSoldAt` returns null when there is no order row,
   * when `subtotal_incl_vat <= 0`, and when the caller is the buyer while
   * `order.state === 'new'`. A sold item could therefore publish InStock.
   *
   * Withdrawn auctions (EXPIRED, CANCELED, BANNED) are not available
   * either, but nobody bought them, so they get OutOfStock rather than
   * SoldOut.
   *
   * Fallback: when `state` is absent (a caller whose fragment does not
   * select it) we keep the old order-derived signal rather than marking a
   * live product OutOfStock on missing data.
   */
  const auctionState = product.auction?.state?.toUpperCase();
  const availability = auctionState
    ? auctionState === "PUBLISHED"
      ? "https://schema.org/InStock"
      : isSoldAuction(product.auction)
      ? "https://schema.org/SoldOut"
      : "https://schema.org/OutOfStock"
    : product.auction?.sold_at
    ? "https://schema.org/SoldOut"
    : "https://schema.org/InStock";

  const url = product.slug
    ? `${CANONICAL_HOST}${productPath(
        product.slug,
        locale || productLocaleDefault
      )}`
    : undefined;

  const brandAttribute = findAttribute(product, "Brand");
  const brandName =
    brandAttribute?.title ?? product.brand_suggestion ?? UNBRANDED;
  const name = buildProductName(product, brandName);

  const colorAttributes = findAttributes(product, "Color");
  const materialAttributes = findAttributes(product, "Material");

  const colors = colorAttributes
    .map(c => c.title)
    .filter((title): title is string => !!title);
  const materials = materialAttributes
    .map(m => m.title)
    .filter((title): title is string => !!title);

  const images = buildProductImageObjects(product, locale);

  // `image` and `name` are required by Merchant listings (WHO-6187: 54
  // items flagged for a missing image). Like the no-price case above, the
  // only valid markup for a listing without them is none.
  if (!images.length || !name) {
    return null;
  }

  const additionalProperty = buildAdditionalProperty(product);
  const categoryPath = buildCategoryPath(product);

  // The listing identifier is the suffix of every PDP URL and the value a
  // buyer quotes to support, so it is the natural `sku`. `productID` is
  // the same value again for consumers that only read that property.
  const identifier = product.identifier || undefined;

  const width = buildQuantitativeValue(product.width);
  const height = buildQuantitativeValue(product.height);
  const depth = buildQuantitativeValue(product.depth);
  const returnPolicy = buildMerchantReturnPolicy(
    product.merchant?.type,
    locale || productLocaleDefault
  );

  return {
    "@context": "https://schema.org/",
    "@type": "Product",
    name,
    description: toPlainDescription(product.description),
    ...(identifier ? { sku: identifier, productID: identifier } : {}),
    image: images,
    ...(width ? { width } : {}),
    ...(height ? { height } : {}),
    ...(depth ? { depth } : {}),
    ...(categoryPath ? { category: categoryPath } : {}),
    // One string per field: Google reads an array here as the field
    // repeated and flags "Duplicate field" (158k color, 125k material
    // items in Merchant listings, 2026-09-29).
    ...(colors.length ? { color: colors.join(MULTI_VALUE_SEPARATOR) } : {}),
    ...(materials.length
      ? { material: materials.join(MULTI_VALUE_SEPARATOR) }
      : {}),
    brand: {
      "@type": "Brand",
      name: brandName,
    },
    ...(additionalProperty.length ? { additionalProperty } : {}),
    offers: {
      "@type": "Offer",
      ...(url ? { url } : {}),
      availability,
      ...(displayPrice.currency
        ? { priceCurrency: displayPrice.currency }
        : {}),
      price: displayPrice.amount.toString(),
      itemCondition: conditionToSchema(product.condition),
      seller: whoppahOrgRef,
      ...(returnPolicy ? { hasMerchantReturnPolicy: returnPolicy } : {}),
    },
  };
};

/**
 * Minimal shape of a similar item, as returned by `searchProducts`
 * through `PublicProductOverviewFragment`.
 */
export type SimilarProductEntry = {
  id?: string | number | null;
  title?: string | null;
  slug?: string | null;
  // Codegen types the array as `(Image | undefined)[]`, so an element can be
  // undefined as well as null. `buildSimilarProductRef` already reads through
  // `image?.url`, so widening the type matches what the code handles.
  images?: Array<{ url?: string | null } | null | undefined> | null;
};

const buildSimilarProductRef = (
  similar: SimilarProductEntry,
  locale: string | undefined
) => {
  if (!similar?.slug || !similar.title) {
    return null;
  }

  const imageUrl = (similar.images ?? []).find(image => !!image?.url)?.url;

  return {
    "@type": "Product",
    name: similar.title,
    url: `${CANONICAL_HOST}${productPath(similar.slug, locale)}`,
    ...(imageUrl ? { image: imageUrl } : {}),
  };
};

/**
 * Product schema for a Soft-Sold page (WHO-4840, GEO-1).
 *
 * Differences vs `buildProductSchema`:
 *  - `Offer.availability` is `SoldOut` rather than `OutOfStock`. Both are
 *    valid, but `SoldOut` is the specific term for a one-off item that
 *    was bought, and it is what stops an assistant reading the page as a
 *    live shopping result.
 *  - `Offer.price` is the ASKING price, and specifically the same one the
 *    page prints as "It was listed at X". The realised price
 *    (`auction.sold_at`) was emitted here originally, on the reasoning that
 *    on a secondhand marketplace the realised price is the interesting
 *    datapoint. It is, which is exactly why it cannot be published: it is
 *    per-item transaction data, it exposes our margin and volume item by
 *    item, and it is the same thing that was ruled out for the Resale
 *    Index. The asking price was public for as long as the listing was
 *    live, so repeating it costs nothing. Nothing on this page, in this
 *    schema, or in `__NEXT_DATA__` carries `sold_at`; see
 *    `redactSoldPrice` in `sold-price.utils.ts`.
 *
 *    The price is inherited from the base schema rather than resolved
 *    again here. It was resolved here at first, because the base schema
 *    published `minimum_bid` whenever bidding was allowed: on
 *    `the-club-lounge-chair-by-hans-kaufeld` that produced a schema
 *    advertising 350 while the page said "It was listed at EUR 400", and
 *    350 also happened to be what the chair sold for, because it went at
 *    the bid floor. The base schema now resolves the asking price through
 *    `resolveAskingPrice` like everything else, so restating it here would
 *    only give the two schemas a way to drift apart again.
 *  - `isSimilarTo` carries the still-buyable alternatives rendered on the
 *    page, so a crawler that lands on a sold listing has a machine
 *    readable route to inventory it can still transact on.
 *
 * `similar` is expected to be already filtered to buyable listings; this
 * function does not check availability.
 */
export const buildSoftSoldProductSchema = (
  product: AnyProduct | undefined | null,
  similar: SimilarProductEntry[] = [],
  locale?: string,
  /**
   * WHO-5240. `SoldOut` is a claim that the item was bought, and it is
   * false for a reserved, banned, expired or cancelled listing. Those
   * pass `OutOfStock`, which says only that it cannot be bought now, and
   * suppress the realised price along with it.
   */
  options: { claimsSale?: boolean } = {}
) => {
  const base = buildProductSchema(product, locale, undefined);

  if (!base) {
    return null;
  }

  const claimsSale = options.claimsSale ?? true;

  const isSimilarTo = similar
    .map(entry => buildSimilarProductRef(entry, locale))
    .filter(Boolean);

  return {
    ...base,
    offers: {
      ...base.offers,
      availability: claimsSale
        ? "https://schema.org/SoldOut"
        : "https://schema.org/OutOfStock",
    },
    ...(isSimilarTo.length ? { isSimilarTo } : {}),
  };
};

const stripHtmlToText = (html: string) =>
  html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

type HelpArticleSchemaInput = {
  title: string;
  summary?: string;
  excerpt?: string;
  bodyHtml: string;
  path: string;
  locale: string;
  updatedAt?: string | null;
};

export const buildHelpArticleSchema = ({
  title,
  summary,
  excerpt,
  bodyHtml,
  path,
  locale,
  updatedAt,
}: HelpArticleSchemaInput) => {
  const canonicalUrl = absoluteUrl(localizePath(path, locale));
  const helpCenterUrl = absoluteUrl(localizePath("/help", locale));
  const description =
    (excerpt ?? "").trim() ||
    (summary ?? "").trim() ||
    stripHtmlToText(bodyHtml).slice(0, 300);

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
    headline: title,
    description,
    ...(updatedAt ? { dateModified: updatedAt } : {}),
    author: whoppahPublisher,
    publisher: whoppahPublisher,
    inLanguage: locale || defaultLocale,
    isPartOf: {
      "@type": "WebPage",
      "@id": helpCenterUrl,
    },
  };
};

export type HelpFaqQuestion = {
  title: string;
  text: string;
  url?: string;
};

type HelpFaqPageSchemaInput =
  | { title: string; bodyHtml: string; url?: string }
  | { questions: HelpFaqQuestion[] };

export const buildHelpFaqPageSchema = (input: HelpFaqPageSchemaInput) => {
  const questions: HelpFaqQuestion[] =
    "questions" in input
      ? input.questions
      : [
          {
            title: input.title,
            text: stripHtmlToText(input.bodyHtml),
            url: input.url,
          },
        ];

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map(question => ({
      "@type": "Question",
      name: question.title,
      ...(question.url ? { url: question.url } : {}),
      acceptedAnswer: {
        "@type": "Answer",
        text: question.text,
      },
    })),
  };
};

export type BreadcrumbSchemaItem = {
  label?: string | null;
  href?: string | null;
};

/**
 * BreadcrumbList for the visible breadcrumb (WHO-6187). Every ListItem
 * needs a `name`, so an item whose label is empty (a category without a
 * title, a listing without a title) is dropped and the positions are
 * renumbered, rather than publishing an invalid item.
 */
export const buildBreadcrumbListSchema = (
  items: BreadcrumbSchemaItem[],
  locale: string | undefined,
  localeDefault: string | undefined = defaultLocale
) => {
  const named = items
    .map(item => ({
      ...item,
      // Titles can arrive entity-encoded ("Brink &amp; Campman").
      label: item.label ? decodeHtmlEntities(item.label).trim() : "",
    }))
    .filter(item => item.label);

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: named.map((item, index) => {
      const url = item.href
        ? new URL(withLocale(item.href, locale, localeDefault), CANONICAL_HOST)
        : undefined;

      return {
        "@type": "ListItem",
        position: index + 1,
        name: item.label,
        ...(url ? { item: url.href } : {}),
      };
    }),
  };
};
